import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Search, Loader2, MessageCircle, MapPin, Briefcase } from "lucide-react";

const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  useAuth(); // Ensure user is authenticated
  const navigate = useNavigate();

  useEffect(() => {
    // Load recent searches from localStorage
    const saved = localStorage.getItem("recentSearches");
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/users/search/${encodeURIComponent(query)}`);
      setResults(response.data);
      
      // Save to recent searches
      const newRecent = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
      setRecentSearches(newRecent);
      localStorage.setItem("recentSearches", JSON.stringify(newRecent));
    } catch (_error) {
      toast.error("Failed to search users");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (u) => {
    if (u.first_name) {
      return `${u.first_name[0]}${(u.last_name || "L")[0]}`.toUpperCase();
    }
    if (u.username) {
      return u.username.slice(0, 2).toUpperCase();
    }
    return u.phone?.slice(-2) || "??";
  };

  const handleStartChat = async (userId) => {
    try {
      const response = await api.post("/conversations", { participant_id: userId });
      navigate(`/messages/${response.data.id}`);
    } catch (_error) {
      toast.error("Failed to start conversation");
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-6 px-4" data-testid="search-page">
        <h1 className="text-2xl font-bold mb-6">Search Users</h1>

        {/* Search Input */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name, city, occupation, company..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              handleSearch(e.target.value);
            }}
            className="pl-10 h-12 text-base"
            data-testid="search-input"
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Search Results */}
        {!loading && results.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              {results.length} user{results.length !== 1 ? 's' : ''} found
            </p>
            {results.map((u) => (
              <Card key={u.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Avatar 
                      className="w-14 h-14 cursor-pointer"
                      onClick={() => navigate(`/user/${u.id}`)}
                    >
                      <AvatarImage src={u.avatar} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {getInitials(u)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigate(`/user/${u.id}`)}
                    >
                      <h3 className="font-semibold truncate">
                        {u.prefix && <span className="text-muted-foreground">{u.prefix}. </span>}
                        {u.first_name || u.username || "User"} {u.last_name || ""}
                      </h3>
                      {u.username && (
                        <p className="text-sm text-muted-foreground">@{u.username}</p>
                      )}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                        {u.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {u.city}
                          </span>
                        )}
                        {u.occupation && (
                          <span className="flex items-center gap-1 capitalize">
                            <Briefcase className="w-3 h-3" /> {u.occupation}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStartChat(u.id)}
                        data-testid={`chat-btn-${u.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => navigate(`/user/${u.id}`)}
                        data-testid={`view-btn-${u.id}`}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && searchQuery && results.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg">No users found</p>
            <p className="text-sm">Try a different search term</p>
          </div>
        )}

        {/* Recent Searches */}
        {!searchQuery && recentSearches.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Recent Searches</h3>
            <div className="flex flex-wrap gap-2">
              {recentSearches.map((search, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchQuery(search);
                    handleSearch(search);
                  }}
                  className="rounded-full"
                >
                  <Search className="w-3 h-3 mr-1" />
                  {search}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!searchQuery && recentSearches.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Search className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg mb-2">Find people on LUNAWAT FOUNDATION</p>
            <p className="text-sm">Search by name, phone, city, occupation, company, and more</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SearchPage;
